import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Container, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { CREATE_GROUP_VERSION_OGMP_CATEGORY_MUTATION } from "../../../../graphql/mutations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

const AddVersionCategoryModal = ({
  show,
  handleClose,
  category,
  parentName,
  groupVersionId,
  technologies,
  handleEnvChange,
  env,
  currentTenant,
  tenantName
}) => {
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    frequency: "",
    dataSource: "",
    measurementTechnologyId: null,
    sampleSize: "",
    sampleSizeUnit: "",
    uncertaintyFraction: "",
    l4Method: ""
  });

  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show, category]);

  const resetForm = () => {
    setFormData({
      frequency: "",
      dataSource: "",
      measurementTechnologyId: null,
      sampleSize: "",
      sampleSizeUnit: "",
      uncertaintyFraction: ""
    });
    setValidationErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || (!isNaN(value) && value.trim() !== "")) {
      setFormData({
        ...formData,
        [name]: value
      });

      if (validationErrors[name]) {
        setValidationErrors((prev) => ({
          ...prev,
          [name]: ""
        }));
      }
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData({
      ...formData,
      [name]: selectedOption ? selectedOption.value : ""
    });

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.frequency) {
      errors.frequency = "Frequency is required";
    }

    if (formData.uncertaintyFraction && (parseFloat(formData.uncertaintyFraction) < 0 || parseFloat(formData.uncertaintyFraction) > 1)) {
      errors.uncertaintyFraction = "Uncertainty Fraction must be between 0 and 1";
    }

    if (formData.sampleSize && !formData.sampleSizeUnit) {
      errors.sampleSizeUnit = "Unit is required when the Sample Size is provided";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onModalClose = () => {
    resetForm();
    handleClose();
  };

  const [createVersionCategory, { loading: mutationLoading }] = useMutation(CREATE_GROUP_VERSION_OGMP_CATEGORY_MUTATION);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const sampleSizeUnit = formData.sampleSize ? formData.sampleSizeUnit : null;

    const versionCategoryInput = {
      groupVersionId,
      ogmpCategoryId: category.id,
      l4Method: formData.l4Method,
      frequency: formData.frequency,
      dataSource: formData?.dataSource,
      measurementTechnologyId: formData?.measurementTechnologyId,
      sampleSize: formData?.sampleSize ? parseFloat(formData.sampleSize) : null,
      sampleSizeUnit: sampleSizeUnit,
      uncertaintyFraction: formData?.uncertaintyFraction ? parseFloat(formData.uncertaintyFraction) : null
    };

    try {
      const { data } = await createVersionCategory({
        variables: {
          versionCategoryInput
        }
      });

      if (data?.createGroupVersionOGMPCategory) {
        onModalClose();
        setTimeout(() => {
          toast.success(`OGMP Category: '${category.name}' added to version successfully.`);
          navigate("/version-categories");
          handleEnvChange(env, currentTenant, tenantName, groupVersionId);
        }, 200);
      }
    } catch (error) {
      console.error("Error adding category:", error);
      onModalClose();
      setTimeout(() => {
        toast.error(`Failed to add category: ${error.message}`);
      }, 200);
    }
  };

  const technologyOptions =
    technologies?.map((tech) => ({
      value: tech.id,
      label: tech.name
    })) || [];

  const frequencyOptions = [
    { value: "Hourly", label: "Hourly" },
    { value: "Daily", label: "Daily" },
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
    { value: "Quarterly", label: "Quarterly" },
    { value: "Yearly", label: "Yearly" }
  ];

  const sampleSizeUnitOptions = [
    { value: "number", label: "Number" },
    { value: "percent", label: "Percentage" }
  ];

  return (
    <Modal show={show} onHide={onModalClose} size="lg" centered>
      <Modal.Header closeButton className="d-flex justify-content-center">
        <Modal.Title className="w-100 text-center">Add OGMP Source Category to Group Version</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit} noValidate>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formCategoryName">
                <Form.Label className="font-weight-bold">
                  <strong>Parent Category</strong>
                </Form.Label>
                <Form.Control type="text" value={parentName || ""} disabled />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formCategoryName">
                <Form.Label className="font-weight-bold">
                  <strong>Category</strong>
                </Form.Label>
                <Form.Control type="text" value={category?.name || ""} disabled />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group controlId="formL4Method">
                <Form.Label className="font-weight-bold">
                  <strong>L4 Method</strong>
                </Form.Label>
                <Form.Control type="text" name="l4Method" value={formData.l4Method} onChange={handleChange} placeholder="Enter L4 Method" />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formUncertaintyFraction">
                <Form.Label className="font-weight-bold">
                  <strong>Uncertainty Fraction</strong>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="uncertaintyFraction"
                  value={formData.uncertaintyFraction}
                  onChange={handleNumberChange}
                  placeholder="Enter Uncertainty Fraction"
                  isInvalid={!!validationErrors.uncertaintyFraction}
                  min="0"
                  max="1"
                  step="0.01"
                />
                <Form.Control.Feedback type="invalid">
                  {typeof validationErrors.uncertaintyFraction === "object"
                    ? validationErrors.uncertaintyFraction.message
                    : validationErrors.uncertaintyFraction}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formFrequency">
                <Form.Label className="font-weight-bold">
                  <strong>Frequency</strong> <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  options={frequencyOptions}
                  value={frequencyOptions.find((option) => option.value === formData.frequency) || null}
                  onChange={(option) => handleSelectChange("frequency", option)}
                  placeholder="Select Frequency (required)"
                  isSearchable
                  className={validationErrors.frequency ? "is-invalid" : ""}
                />
                {validationErrors.frequency && (
                  <div className="text-danger mt-1 small">
                    {typeof validationErrors.frequency === "object" ? validationErrors.frequency.message : validationErrors.frequency}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="formMeasurementTechnology">
                <Form.Label className="font-weight-bold">
                  <Container>
                    <strong>Measurement Technology</strong>
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id="info-tooltip" className="text-start">
                          You can add new measurement technology in the Measurements tab.
                        </Tooltip>
                      }
                    >
                      <span style={{ cursor: "pointer", marginLeft: "5px" }}>
                        <FontAwesomeIcon icon={faInfoCircle} className="text-primary" style={{ fontSize: "16px" }} />
                      </span>
                    </OverlayTrigger>
                  </Container>
                </Form.Label>
                <Select
                  options={technologyOptions}
                  value={technologyOptions.find((option) => option.value === formData.measurementTechnologyId) || null}
                  onChange={(option) => handleSelectChange("measurementTechnologyId", option)}
                  placeholder="Select Technology"
                  isClearable
                  isSearchable
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formDataSource">
                <Form.Label className="font-weight-bold">
                  <strong>Data Source</strong>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="dataSource"
                  value={formData.dataSource}
                  onChange={handleChange}
                  placeholder="Enter data source"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="formSampleSize">
                <Form.Label className="font-weight-bold">
                  <strong>Sample Size</strong>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="sampleSize"
                  value={formData.sampleSize}
                  onChange={handleNumberChange}
                  placeholder="Enter sample size"
                  isInvalid={!!validationErrors.sampleSize}
                  min="0"
                  max="5000000"
                  step="5"
                />
                <Form.Control.Feedback type="invalid">
                  {typeof validationErrors.sampleSize === "object" ? validationErrors.sampleSize.message : validationErrors.sampleSize}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="formSampleSizeUnit">
                <Form.Label className="font-weight-bold">
                  <strong>Sample Size Unit</strong> {formData.sampleSize && <span className="text-danger">*</span>}
                </Form.Label>
                <Select
                  options={sampleSizeUnitOptions}
                  value={sampleSizeUnitOptions.find((option) => option.value === formData.sampleSizeUnit) || null}
                  onChange={(option) => handleSelectChange("sampleSizeUnit", option)}
                  placeholder="Select Unit"
                  isClearable
                  isSearchable
                  className={validationErrors.sampleSizeUnit ? "is-invalid" : ""}
                />
                {validationErrors.sampleSizeUnit && (
                  <div className="text-danger mt-1 small">
                    {typeof validationErrors.sampleSizeUnit === "object"
                      ? validationErrors.sampleSizeUnit.message
                      : validationErrors.sampleSizeUnit}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <div className="d-grid">
            <Button variant="primary" type="submit" style={{ width: "50%" }} className="mx-auto" disabled={mutationLoading}>
              {mutationLoading ? "Assigning category to version..." : "Assign to Version"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddVersionCategoryModal;
